// ── PoemSessionManager ────────────────────────────────────────────────────────
// Single point of truth for reading and writing per-poem AI sessions.
// All persistence goes through poem-session-db.js — callers never touch storage.
//
// Isolation guarantee: every operation is scoped to a single poemId.
// Sessions for different poems are stored as independent IDB records — there is
// no shared mutable state between them.
//
// Timestamps are Unix epoch milliseconds (Date.now()), not ISO strings.
// This makes comparisons, sorting, and age-checks cheaper and avoids locale
// parsing edge-cases when the stored value is read back.

import { dbGetSession, dbPutSession, dbDeleteSession } from './poem-session-db.js';

/** @import { PoemSession, VerseHistoryEntry } from './poem-session-types.js' */

// ── Helpers ───────────────────────────────────────────────────────────────────

/** @param {string} poemId @returns {PoemSession} */
function emptySession(poemId) {
  return {
    poemId,
    runningSummary: '',
    lastVerseId:    null,
    verseHistory:   [],
    lastUpdatedAt:  Date.now(),
  };
}

// ── Manager class ─────────────────────────────────────────────────────────────

class PoemSessionManager {
  /**
   * Returns the stored session for a poem.
   * If no session exists yet, returns a fresh empty session (does NOT persist it).
   *
   * @param {string} poemId
   * @returns {Promise<PoemSession>}
   */
  async getSession(poemId) {
    const stored = await dbGetSession(poemId);
    return stored ?? emptySession(poemId);
  }

  /**
   * Persists a session object directly.
   * Use this when you need fine-grained control over the session shape.
   * Most callers should prefer updateRunningSummary instead.
   *
   * @param {PoemSession} session
   * @returns {Promise<void>}
   */
  async saveSession(session) {
    await dbPutSession(session);
  }

  /**
   * Updates the running summary for a poem after the user pastes back an AI response.
   *
   * - Sets runningSummary = newSummary
   * - Sets lastVerseId = verseId
   * - Upserts a verseHistory entry for verseId (replaces an existing entry for the
   *   same verseId so re-saving the same verse is idempotent)
   * - Updates lastUpdatedAt to Date.now()
   * - Persists via saveSession
   *
   * @param {string} poemId
   * @param {string} newSummary  - The text pasted by the user (AI's "Summary to Paste Back")
   * @param {string} verseId     - ID of the verse that was just analysed
   * @returns {Promise<PoemSession>}  The updated session, after writing
   */
  async updateRunningSummary(poemId, newSummary, verseId) {
    const session = await this.getSession(poemId);

    /** @type {VerseHistoryEntry} */
    const newEntry = {
      verseId,
      summarySnapshot: newSummary,
      timestamp:       Date.now(),
    };

    // Upsert: replace the existing entry for this verseId, or append if new.
    const verseHistory = [
      ...session.verseHistory.filter(e => e.verseId !== verseId),
      newEntry,
    ];

    /** @type {PoemSession} */
    const updated = {
      ...session,
      runningSummary: newSummary,
      lastVerseId:    verseId,
      verseHistory,
      lastUpdatedAt:  Date.now(),
    };

    await this.saveSession(updated);
    return updated;
  }

  /**
   * Stores the poem-level overview (from Prompt 1 paste-back).
   * Written once per poem; calling again replaces the previous value.
   *
   * @param {string} poemId
   * @param {string} overview  - The "Poem Overview to Paste Back:" block the user pasted
   * @returns {Promise<PoemSession>}
   */
  async updatePoemOverview(poemId, overview) {
    const session = await this.getSession(poemId);
    const updated = { ...session, poemOverview: overview, lastUpdatedAt: Date.now() };
    await this.saveSession(updated);
    return updated;
  }

  /**
   * Stores user-written additional notes for a poem.
   *
   * @param {string} poemId
   * @param {string} notes
   * @returns {Promise<PoemSession>}
   */
  async updateAdditionalNotes(poemId, notes) {
    const session = await this.getSession(poemId);
    const updated = { ...session, additionalNotes: notes, lastUpdatedAt: Date.now() };
    await this.saveSession(updated);
    return updated;
  }

  /**
   * Clears only the poem overview, leaving verse history and notes intact.
   *
   * @param {string} poemId
   * @returns {Promise<PoemSession>}
   */
  async clearPoemOverview(poemId) {
    const session = await this.getSession(poemId);
    const updated = { ...session, poemOverview: '', lastUpdatedAt: Date.now() };
    await this.saveSession(updated);
    return updated;
  }

  /**
   * Clears only verse history and running summary, leaving overview and notes intact.
   *
   * @param {string} poemId
   * @returns {Promise<PoemSession>}
   */
  async clearVerseHistory(poemId) {
    const session = await this.getSession(poemId);
    const updated = {
      ...session,
      runningSummary: '',
      verseHistory:   [],
      lastVerseId:    null,
      lastUpdatedAt:  Date.now(),
    };
    await this.saveSession(updated);
    return updated;
  }

  /**
   * Removes all AI session data for a poem (non-reversible).
   * A subsequent getSession() call will return a fresh empty session.
   *
   * @param {string} poemId
   * @returns {Promise<void>}
   */
  async clearSession(poemId) {
    await dbDeleteSession(poemId);
  }
}

// Singleton — one instance across the app so the IDB connection is reused.
export const poemSessionManager = new PoemSessionManager();
